import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@trovata/environments/environment';
import { StablecoinPage } from '../../models/stablecoin.model';
import {
	StablecoinApprovalDecisionValue,
	StablecoinApprovalPolicy,
	StablecoinApprovalState,
	StablecoinTransfer,
	StablecoinTransferCreate,
	StablecoinTransferStatus,
} from '../../models/transfer.model';
import { StablecoinDestination, StablecoinDestinationCreate } from '../../models/destination.model';
import { StablecoinActivityFilter } from '../../models/report.model';

/**
 * Low-level HTTP service for stablecoin transfers, approvals, and destinations.
 * State/components should use StablecoinTransfersFacadeService instead.
 */
@Injectable({
	providedIn: 'root',
})
export class StablecoinTransfersService {
	private readonly http: HttpClient = inject(HttpClient);
	private readonly baseUrl: string = environment.stablecoinAPI();

	getTransfers(filter?: StablecoinActivityFilter, cursor?: string, pageSize?: number): Observable<StablecoinPage<StablecoinTransfer>> {
		let params: HttpParams = new HttpParams();
		if (filter) {
			Object.entries(filter).forEach(([key, value]: [string, string | null | undefined]) => {
				if (value !== null && value !== undefined && value !== '') {
					params = params.set(key, value);
				}
			});
		}
		if (cursor) {
			params = params.set('page_cursor', cursor);
		}
		if (pageSize) {
			params = params.set('page_size', pageSize.toString());
		}
		return this.http.get<StablecoinPage<StablecoinTransfer>>(`${this.baseUrl}/transfers`, { params });
	}

	getNeedsApprovalTransfers(): Observable<StablecoinPage<StablecoinTransfer>> {
		const params: HttpParams = new HttpParams().set('status', StablecoinTransferStatus.pendingApproval).set('page_size', '500');
		return this.http.get<StablecoinPage<StablecoinTransfer>>(`${this.baseUrl}/transfers`, { params });
	}

	createTransfer(body: StablecoinTransferCreate, idempotencyKey: string): Observable<StablecoinTransfer> {
		const headers: HttpHeaders = new HttpHeaders({ 'Idempotency-Key': idempotencyKey });
		return this.http.post<StablecoinTransfer>(`${this.baseUrl}/transfers`, body, { headers });
	}

	cancelTransfer(transferId: string): Observable<StablecoinTransfer> {
		return this.http.post<StablecoinTransfer>(`${this.baseUrl}/transfers/${transferId}/cancel`, {});
	}

	/** Returns the transfer's approval requests, oldest first (one per workflow stage; a single null-stage entry for legacy policies). */
	getTransferApprovals(transferId: string): Observable<StablecoinApprovalState[]> {
		return this.http.get<StablecoinApprovalState[]>(`${this.baseUrl}/transfers/${transferId}/approvals`);
	}

	submitTransferApproval(transferId: string, decision: StablecoinApprovalDecisionValue, comment?: string): Observable<StablecoinTransfer> {
		return this.http.post<StablecoinTransfer>(`${this.baseUrl}/transfers/${transferId}/approvals`, { decision, comment: comment ?? null });
	}

	getDestinations(): Observable<StablecoinDestination[]> {
		return this.http.get<StablecoinDestination[]>(`${this.baseUrl}/destinations`);
	}

	createDestination(body: StablecoinDestinationCreate): Observable<StablecoinDestination> {
		return this.http.post<StablecoinDestination>(`${this.baseUrl}/destinations`, body);
	}

	submitDestinationApproval(destinationId: string, decision: StablecoinApprovalDecisionValue, comment?: string): Observable<StablecoinDestination> {
		return this.http.post<StablecoinDestination>(`${this.baseUrl}/destinations/${destinationId}/approvals`, {
			decision,
			comment: comment ?? null,
		});
	}

	disableDestination(destinationId: string): Observable<StablecoinDestination> {
		return this.http.post<StablecoinDestination>(`${this.baseUrl}/destinations/${destinationId}/disable`, {});
	}

	getApprovalPolicy(): Observable<StablecoinApprovalPolicy> {
		return this.http.get<StablecoinApprovalPolicy>(`${this.baseUrl}/approval-policies`);
	}
}
